// ================================================================
// 💰 FINANCIAL REPORT - Relatório Financeiro
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import '../../components/reports/Reports.css';
import { useChartsColors } from '../../hooks/useChartsColors';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);

const FinancialReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartsColors = useChartsColors();

  const loadFinancialReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('💰 Carregando Relatório Financeiro com filtros...', filtersToUse);

      const result = await ReportsService.getFinancialReport({
        period: filtersToUse.period,
        product_id: filtersToUse.advancedFilters?.product_id
      });

      if (result.success) {
        setFinancialData(result.data);
        console.log('✅ Relatório Financeiro carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFinancialReport = useCallback(async () => {
    loadFinancialReportWithFilters(filters);
  }, [filters, loadFinancialReportWithFilters]);

  useEffect(() => {
    loadFinancialReport();
  }, [loadFinancialReport]);

  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => {
      const newFilters = { ...prev, period: newPeriod };
      setTimeout(() => loadFinancialReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev, advancedFilters };
      setTimeout(() => loadFinancialReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const revenueTrendData = financialData?.charts?.revenueTrend ? {
    labels: financialData.charts.revenueTrend.map(item => new Date(item.date).toLocaleDateString('pt-BR')),
    datasets: [{
      label: 'Receita Bruta',
      data: financialData.charts.revenueTrend.map(item => item.gross_revenue / 100),
      borderColor: '#007bff',
      backgroundColor: 'rgba(0, 123, 255, 0.1)',
      tension: 0.4
    }, {
      label: 'Receita Líquida',
      data: financialData.charts.revenueTrend.map(item => item.net_revenue / 100),
      borderColor: '#28a745',
      backgroundColor: 'rgba(40, 167, 69, 0.1)',
      tension: 0.4
    }]
  } : { labels: [], datasets: [] };

  const expensesBreakdownData = financialData?.charts?.expensesBreakdown ? {
    labels: financialData.charts.expensesBreakdown.map(item => item.expense_type),
    datasets: [{
      label: 'Valor',
      data: financialData.charts.expensesBreakdown.map(item => item.total_amount / 100),
      backgroundColor: ['#dc3545', '#ffc107', '#17a2b8', '#6f42c1', '#fd7e14'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  } : { labels: [], datasets: [] };

  const transactionsColumns = [
    {
      key: 'transaction_date',
      label: 'Data',
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString('pt-BR')
    },
    {
      key: 'transaction_type',
      label: 'Tipo',
      sortable: true,
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: value === 'Venda' ? '#d4edda' : '#fff3cd',
          color: value === 'Venda' ? '#155724' : '#856404'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'amount_formatted',
      label: 'Valor',
      sortable: true,
      render: (value, row) => (
        <span style={{ color: row.is_expense ? '#dc3545' : '#28a745', fontWeight: '600' }}>
          {row.is_expense ? '-' : '+'}{value}
        </span>
      )
    },
    {
      key: 'category',
      label: 'Categoria',
      sortable: true
    },
    {
      key: 'product_name',
      label: 'Produto',
      sortable: true
    },
    {
      key: 'customer_name',
      label: 'Cliente',
      sortable: true
    }
  ];

  return (
    <MasterLayout>
      <Container fluid className="reports-container" style={{ padding: '24px', maxWidth: '100%' }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon icon="mdi:currency-usd" style={{ fontSize: '22px', color: '#28a745' }} />
                <span className='text-contrast' style={{ fontSize: '22px', fontWeight: '700', display: 'block' }}>
                  Relatório Financeiro
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="outline-primary"
                  onClick={() => setShowFilterDrawer(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:filter-variant" style={{ fontSize: '20px' }} />
                  Filtros Avançados
                  {Object.keys(filters.advancedFilters).length > 0 && (
                    <span style={{
                      backgroundColor: '#007bff',
                      color: '#fff',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {Object.keys(filters.advancedFilters).length}
                    </span>
                  )}
                </Button>
                <Button
                  variant="outline-success"
                  onClick={() => console.log('Exportar CSV')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:file-excel" style={{ fontSize: '20px' }} />
                  Exportar CSV
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={loadFinancialReport}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                >
                  <Icon icon="mdi:refresh" style={{ fontSize: '20px' }} />
                  Atualizar
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filter Bar */}
        <FilterBar
          period={filters.period}
          onPeriodChange={handlePeriodChange}
          onApplyAdvancedFilters={handleAdvancedFilters}
          onRefresh={loadFinancialReport}
          loading={loading}
          advancedFilters={filters.advancedFilters}
          onUserFilterChange={() => {}}
        />

        <hr style={{ margin: '24px 0', borderTop: '2px solid #dee2e6' }} />

        {/* Error State */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p style={{ marginTop: '16px', color: '#6c757d' }}>Carregando dados financeiros...</p>
          </div>
        )}

        {/* Main Content */}
        {!loading && financialData && (
          <>
            {/* KPIs */}
            <Row className="mb-4">
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={financialData.kpis.gross_revenue.label}
                  value={financialData.kpis.gross_revenue.formatted}
                  icon="mdi:trending-up"
                  color="#007bff"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={financialData.kpis.platform_fee.label}
                  value={financialData.kpis.platform_fee.formatted}
                  icon="mdi:bank"
                  color="#dc3545"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={financialData.kpis.commissions_paid.label}
                  value={financialData.kpis.commissions_paid.formatted}
                  icon="mdi:account-cash"
                  color="#ffc107"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={financialData.kpis.net_revenue.label}
                  value={financialData.kpis.net_revenue.formatted}
                  icon="mdi:currency-usd"
                  color="#28a745"
                />
              </Col>
            </Row>

            {/* Charts */}
            <Row className="mb-4">
              <Col md={8} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Tendência de Receita
                    </span>
                    <Line data={revenueTrendData} options={{ responsive: true, maintainAspectRatio: true, scales: { x: { grid: { color: chartsColors } }, y: { grid: { color: chartsColors }}} }} />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Breakdown de Despesas
                    </span>
                    <Doughnut data={expensesBreakdownData} options={{ responsive: true, maintainAspectRatio: true }} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Transactions Table */}
            <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Card.Body>
                <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                  Lista de Transações
                </span>
                <SmartTable
                  columns={transactionsColumns}
                  data={financialData.transactionsList || []}
                  searchable={true}
                  pagination={true}
                  itemsPerPage={10}
                />
              </Card.Body>
            </Card>
          </>
        )}

        {/* Filter Drawer */}
        <FilterDrawer
          show={showFilterDrawer}
          onHide={() => setShowFilterDrawer(false)}
          onApply={(filters) => {
            setShowFilterDrawer(false);
            handleAdvancedFilters(filters);
          }}
          currentFilters={filters.advancedFilters}
        />
      </Container>
    </MasterLayout>
  );
};

export default FinancialReport;

